import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Topbar } from "../components/Topbar";
import { StatusBadge } from "../components/StatusBadge";
import { RequirementDrawer } from "../components/RequirementDrawer";
import {
  getProperty,
  getOwner,
  getRequirementsForProperty,
  getDocumentsForProperty,
  getContactsForProperty,
  PROP_STATUS_TONE,
  REQ_STATUS_LABELS,
  REQ_STATUS_TONE,
  STATUS_LABELS,
} from "../data/mockData";
import {
  ChevronLeft,
  Download,
  FileText,
  Mail,
  Phone,
  MapPin,
  Building2,
  Users,
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { LockedScreen } from "../components/preview/LockedScreen";
import { PROPERTIES } from "../data/mockData";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "requirements", label: "Requirements" },
  { id: "documents", label: "Documents" },
  { id: "contacts", label: "Contacts" },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const isFirstProperty = id === PROPERTIES[0].id;
  return (
    <LockedScreen locked={!isFirstProperty}>
      <PropertyDetailInner />
    </LockedScreen>
  );
}

function PropertyDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const property = getProperty(id);
  const [tab, setTab] = useState("overview");
  const [selectedReqId, setSelectedReqId] = useState(null);

  const requirements = useMemo(
    () => (property ? getRequirementsForProperty(property.id) : []),
    [property]
  );
  const documents = useMemo(
    () => (property ? getDocumentsForProperty(property.id) : []),
    [property]
  );
  const contacts = useMemo(
    () => (property ? getContactsForProperty(property.id) : []),
    [property]
  );

  if (!property) {
    return (
      <>
        <Topbar title="Property not found" />
        <div className="p-8">
          <Link
            to="/properties"
            className="text-sm text-slate-600 dark:text-slate-400 hover:underline"
          >
            ← Back to properties
          </Link>
        </div>
      </>
    );
  }

  const owner = getOwner(property.ownerId);
  const selectedReq = requirements.find((r) => r.id === selectedReqId);

  return (
    <>
      <Topbar
        title={property.name}
        subtitle={`${property.city}, ${property.country} · ${property.reference}`}
      />

      <div className="px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 mb-4"
          data-testid="back-btn"
        >
          <ChevronLeft size={14} /> Back
        </button>

        {/* Summary strip */}
        <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden mb-6">
          <div className="bg-slate-900 dark:bg-slate-950 relative px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.7) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">
                {property.reference} · {property.operationalStatus}
              </p>
              <h2 className="font-display text-3xl text-white font-semibold tracking-tight mt-1">
                {property.name}
              </h2>
              <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1">
                  <MapPin size={12} /> {property.address}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users size={12} /> {property.capacity} guests · {property.bedrooms} bd
                </span>
              </div>
            </div>
            <div className="relative">
              <StatusBadge
                tone={PROP_STATUS_TONE[property.complianceStatus]}
                className="text-sm px-2.5 py-1"
              >
                {STATUS_LABELS[property.complianceStatus]}
              </StatusBadge>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 border-t border-slate-200 dark:border-slate-800">
            {[
              ["Requirements", property.requirementCounts.total, ""],
              ["Current", property.requirementCounts.current, "text-emerald-700 dark:text-emerald-400"],
              ["Expiring", property.requirementCounts.expiring, "text-amber-700 dark:text-amber-400"],
              ["Missing", property.requirementCounts.missing, "text-rose-700 dark:text-rose-400"],
              ["Next expiry", property.nextExpiryDate || "—", "text-slate-900 dark:text-slate-100"],
            ].map(([label, value, tone], i) => (
              <div
                key={label}
                className={cn(
                  "px-6 py-4 border-slate-200 dark:border-slate-800",
                  i > 0 && "md:border-l",
                  i > 0 && i % 2 === 0 && "border-t md:border-t-0"
                )}
              >
                <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
                <p
                  className={cn(
                    "font-display text-2xl font-semibold tabular-nums mt-1",
                    tone || "text-slate-900 dark:text-slate-100"
                  )}
                >
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 dark:border-slate-800 mb-6">
          <nav className="flex gap-6" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={cn(
                  "pb-3 text-sm transition-colors relative",
                  tab === t.id
                    ? "text-slate-900 dark:text-slate-100 font-medium"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                )}
              >
                {t.label}
                {tab === t.id && (
                  <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-slate-900 dark:bg-slate-100" />
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        {tab === "overview" && (
          <section
            data-testid="tab-panel-overview"
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            <div className="lg:col-span-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
              <h3 className="text-[11px] uppercase tracking-widest text-slate-500 mb-4">
                Property details
              </h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <Field label="Address" value={property.address} />
                <Field label="City" value={`${property.city}, ${property.country}`} />
                <Field label="Reference" value={property.reference} mono />
                <Field label="Operational status" value={property.operationalStatus} />
                <Field label="Bedrooms" value={property.bedrooms} />
                <Field label="Max capacity" value={`${property.capacity} guests`} />
              </dl>
            </div>
            <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
              <h3 className="text-[11px] uppercase tracking-widest text-slate-500 mb-4">Owner</h3>
              <p className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">
                {owner?.name}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {owner?.company}
              </p>
              <div className="mt-4 space-y-2 text-sm">
                <a
                  href={`mailto:${owner?.email}`}
                  className="flex items-center gap-2 text-slate-700 dark:text-slate-300"
                >
                  <Mail size={13} /> {owner?.email}
                </a>
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Phone size={13} /> {owner?.phone}
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === "requirements" && (
          <section
            data-testid="tab-panel-requirements"
            className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/40 text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    <th className="py-3 px-6 text-left">Requirement</th>
                    <th className="py-3 px-4 text-left">Category</th>
                    <th className="py-3 px-4 text-left">Authority</th>
                    <th className="py-3 px-4 text-left">Reference</th>
                    <th className="py-3 px-4 text-left">Expiry</th>
                    <th className="py-3 px-4 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {requirements.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`req-row-${r.id}`}
                      onClick={() => setSelectedReqId(r.id)}
                      className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-6 font-medium text-slate-900 dark:text-slate-100">
                        {r.name}
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{r.category}</td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{r.authority}</td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 font-mono text-xs">
                        {r.referenceNumber || "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {r.expiryDate || "—"}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge tone={REQ_STATUS_TONE[r.status]}>
                          {REQ_STATUS_LABELS[r.status]}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "documents" && (
          <section
            data-testid="tab-panel-documents"
            className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/40 text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    <th className="py-3 px-6 text-left">Document</th>
                    <th className="py-3 px-4 text-left">Type</th>
                    <th className="py-3 px-4 text-left">Uploaded</th>
                    <th className="py-3 px-4 text-left">Expiry</th>
                    <th className="py-3 px-4 text-left">Size</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-sm text-slate-500">
                        No documents on file yet.
                      </td>
                    </tr>
                  )}
                  {documents.map((d) => (
                    <tr
                      key={d.id}
                      className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-slate-500" />
                          <span className="text-slate-900 dark:text-slate-100">{d.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{d.type}</td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {d.uploadedAt}
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {d.expiryDate || "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-500 tabular-nums">{d.sizeKb} KB</td>
                      <td className="py-3 px-4">
                        <button
                          data-testid={`doc-download-${d.id}`}
                          onClick={() => toast.success(`Downloading ${d.name}`)}
                          className="h-8 w-8 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          <Download size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "contacts" && (
          <section
            data-testid="tab-panel-contacts"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          >
            {contacts.map((c) => (
              <div
                key={c.id}
                className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
              >
                <p className="font-medium text-slate-900 dark:text-slate-100">{c.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {c.role} · {c.company}
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <a
                    href={`mailto:${c.email}`}
                    className="flex items-center gap-2 text-slate-700 dark:text-slate-300"
                  >
                    <Mail size={13} /> {c.email}
                  </a>
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <Phone size={13} /> {c.phone}
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </div>

      <RequirementDrawer
        requirement={selectedReq}
        open={Boolean(selectedReq)}
        onClose={() => setSelectedReqId(null)}
      />
    </>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd
        className={cn(
          "text-slate-900 dark:text-slate-100 mt-0.5",
          mono && "font-mono text-xs"
        )}
      >
        {value}
      </dd>
    </div>
  );
}
